import { useCallback, useEffect, useRef, useState } from "react";

// Zoom and pan for the fullscreen world map.
// - mouse wheel / trackpad pinch: zoom around the cursor
// - drag: pan (once zoomed in); two-finger pinch on touch screens
// - zoomBy(): the on-screen +/- buttons, around the centre
// The view is a CSS transform on the map's inner layer (innerStyle), eased
// between steps and applied directly while a finger or mouse is moving it.
// A drag that moves more than a few pixels swallows the click that follows,
// so panning never counts as a click on a pin or a hidden key.
// `rotated`: the map is shown turned 90 degrees clockwise (phone held upright
// in fullscreen), so screen coordinates are mapped back into the map's own.
export const MAX_ZOOM = 6;
const IDENTITY = { z: 1, x: 0, y: 0 };
const DRAG_PX = 5;

const useMapZoom = (enabled, rotated = false) => {
  const rotatedRef = useRef(rotated);
  rotatedRef.current = rotated;
  const boxRef = useRef(null);
  const [view, setView] = useState(IDENTITY);
  const [live, setLive] = useState(false); // gesture in progress: no easing
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const moved = useRef(false);

  // Layout size of the map box (unaffected by the rotation).
  const size = () => {
    const el = boxRef.current;
    return el ? { w: el.clientWidth || 1, h: el.clientHeight || 1 } : { w: 1, h: 1 };
  };
  // A pointer's position in the map box's own (unrotated) coordinates.
  const point = (e) => {
    const r = boxRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    return rotatedRef.current ? { x: e.clientY - r.top, y: r.right - e.clientX } : { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  // Keep the map covering the box: no panning past its edges.
  const clampView = ({ z, x, y }) => {
    const { w, h } = size();
    const zz = Math.min(MAX_ZOOM, Math.max(1, z));
    return { z: zz, x: Math.min(0, Math.max(w - w * zz, x)), y: Math.min(0, Math.max(h - h * zz, y)) };
  };
  const zoomAround = useCallback((v, factor, mx, my) => {
    const z = Math.min(MAX_ZOOM, Math.max(1, v.z * factor));
    const k = z / v.z;
    return clampView({ z, x: mx - (mx - v.x) * k, y: my - (my - v.y) * k });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = useCallback(() => setView(IDENTITY), []);
  const zoomBy = useCallback(
    (factor) => {
      const { w, h } = size();
      setView((v) => zoomAround(v, factor, w / 2, h / 2));
    },
    [zoomAround]
  );

  useEffect(() => {
    if (!enabled) {
      setView(IDENTITY);
      return undefined;
    }
    const box = boxRef.current;
    if (!box) return undefined;
    // Non-passive so the page itself doesn't scroll or zoom.
    const onWheel = (e) => {
      e.preventDefault();
      const { x, y } = point(e);
      // ctrlKey is set for trackpad pinches, which send much smaller deltas.
      const factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.012 : 0.0018));
      setView((v) => zoomAround(v, factor, x, y));
    };
    box.addEventListener("wheel", onWheel, { passive: false });
    return () => box.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, zoomAround]);

  const startGesture = (v) => {
    const pts = [...pointers.current.values()];
    if (pts.length >= 2) {
      const [a, b] = pts;
      gesture.current = {
        type: "pinch",
        view: v,
        dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      };
    } else if (pts.length === 1) {
      gesture.current = { type: "pan", view: v, start: pts[0] };
    } else {
      gesture.current = null;
    }
  };

  const handlers = enabled
    ? {
        onPointerDown: (e) => {
          pointers.current.set(e.pointerId, point(e));
          moved.current = false;
          startGesture(view);
        },
        onPointerMove: (e) => {
          if (!pointers.current.has(e.pointerId)) return;
          pointers.current.set(e.pointerId, point(e));
          const g = gesture.current;
          if (!g) return;
          const pts = [...pointers.current.values()];
          if (g.type === "pan") {
            const dx = pts[0].x - g.start.x;
            const dy = pts[0].y - g.start.y;
            if (!moved.current && Math.hypot(dx, dy) < DRAG_PX) return;
            moved.current = true;
            if (g.view.z <= 1) return;
            setLive(true);
            setView(clampView({ ...g.view, x: g.view.x + dx, y: g.view.y + dy }));
          } else if (pts.length >= 2) {
            const [a, b] = pts;
            const dist = Math.hypot(a.x - b.x, a.y - b.y);
            const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
            moved.current = true;
            setLive(true);
            const z = Math.min(MAX_ZOOM, Math.max(1, (g.view.z * dist) / g.dist));
            const k = z / g.view.z;
            setView(clampView({ z, x: mid.x - (g.mid.x - g.view.x) * k, y: mid.y - (g.mid.y - g.view.y) * k }));
          }
        },
        onPointerUp: (e) => {
          pointers.current.delete(e.pointerId);
          setView((v) => {
            startGesture(v);
            return v;
          });
          if (!pointers.current.size) setLive(false);
        },
        onPointerCancel: (e) => {
          pointers.current.delete(e.pointerId);
          if (!pointers.current.size) {
            gesture.current = null;
            setLive(false);
          }
        },
        onClickCapture: (e) => {
          if (moved.current) {
            e.stopPropagation();
            e.preventDefault();
            moved.current = false;
          }
        }
      }
    : {};

  const innerStyle = {
    transform: `translate(${view.x}px, ${view.y}px) scale(${view.z})`,
    transformOrigin: "0 0",
    // No will-change: it would freeze the SVG dots as a bitmap and blur them
    // when scaled; without it the browser redraws them sharp at each zoom.
    transition: live ? "none" : "transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1)"
  };

  return { boxRef, view, innerStyle, handlers, zoomBy, reset, dragging: live };
};

export default useMapZoom;
